// components
import { GridColDef, GridValidRowModel } from '@mui/x-data-grid-pro';
import { Period } from 'components/shared/index';
import EmployeeHeader from '../components/EmployeeHeader';
import EmployeeCell from '../components/EmployeeCell';
import DayHeader from '../components/DayHeader';
import DutyCell from '../components/DutyCell';
import { Icon } from '@sdb/planning-ui';
import { capitalize, Tooltip, Typography } from '@mui/material';

// external libraries
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// styles
import {
  StyledAbsenceBox,
  StyledDutyCellBox,
  StyledEntityColorIndicator,
  StyledOpenRequestFunctionBox,
  StyledTypographyFontMedium
} from './useGetColumns.styles';
import { StyledTypographyNormalLineHeightSemiBold } from 'components/shared/Typography/Typography.styles';
import { IconContainer } from 'components/shared/Header/Header.styles';
import { StyledBoxFlexSmallGap } from 'components/shared/Box/Box.styles';

// types
import { QualificationResponse } from 'RestAPIs/roster/qualifications/types';
import { EmployeeDay } from 'RestAPIs/cost-centers/employees/types';
import {
  CostCentersFromOthersPrefValue,
  RowDisplayStatus
} from './useGetColumns.types';
import { EmployeeNameFormatTypes } from '../RosterPlan.types';

// utils
import { dataChipValue, formatEmployeeName } from './useGetColumns.utils';
import dayjs from 'dayjs';

export const useGetColumns = ({
  datesToDisplay,
  period = 'month',
  isOpenRequests = false,
  qualificationsByCostCenters = [],
  showSourceCostCenter,
  showCommitmentCostCenter,
  costCenterFromOthersPrefValue,
  rosterPlanColumnsVisibility,
  employeeNameFormat,
  showSortOrderColumn = false
}: {
  datesToDisplay: string[];
  period: Period | null;
  isOpenRequests?: boolean;
  qualificationsByCostCenters?: QualificationResponse[];
  showSourceCostCenter?: boolean;
  showCommitmentCostCenter?: boolean;
  costCenterFromOthersPrefValue?: CostCentersFromOthersPrefValue;
  rosterPlanColumnsVisibility?: { [key: string]: boolean };
  employeeNameFormat?: EmployeeNameFormatTypes;
  showSortOrderColumn?: boolean;
}) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'rosterPlan'
  });

  const weekOverview = period === 'week';
  const fourWeeksOverview = period === 'fourWeeks';

  const {
    showCommitmentHours,
    showBalanceWeek,
    showBalanceMonth,
    showCumulativeBalance,
    showWorkedBalance,
    showLeaveBalance,
    showYearlyLeaveBalance,
    showPlbBalance,
    showPlbYearBalance
  } = rosterPlanColumnsVisibility || {};

  const commonFieldProps: GridValidRowModel = {
    width: 70,
    headerAlign: 'center',
    align: 'center'
  };

  const PLACEHOLDER_KEYS = [
    ...(showBalanceWeek ? ['balanceWeek'] : []),
    ...(showBalanceMonth ? ['balance'] : []),
    ...(showCumulativeBalance ? ['cumulative'] : []),
    ...(showWorkedBalance ? ['worked'] : []),
    ...(showLeaveBalance ? ['leave'] : []),
    ...(showYearlyLeaveBalance ? ['year'] : []),
    ...(showPlbBalance ? ['plb'] : []),
    ...(showPlbYearBalance ? ['plbYear'] : [])
  ];

  const makeEmptyCol = (id: string): GridColDef<GridValidRowModel> => ({
    field: id,
    headerName: '',
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    resizable: false,
    align: 'center',
    headerAlign: 'center',
    width: 70,
    renderHeader: () => '',
    renderCell: () => null
  });

  return useMemo<GridColDef<GridValidRowModel>[]>(
    () => [
      ...(showSortOrderColumn
        ? [
            {
              field: 'sortorder',
              headerName: '#',
              width: 50,
              align: 'center',
              headerAlign: 'center'
            } as GridColDef<GridValidRowModel>
          ]
        : []),
      ...(isOpenRequests
        ? [
            {
              field: 'functionName',
              headerName: t('dataGrid.functions'),
              minWidth: 220,
              flex: 1,
              renderHeader: () => (
                <StyledTypographyNormalLineHeightSemiBold variant="subtitle1">
                  {t('dataGrid.functions')}
                </StyledTypographyNormalLineHeightSemiBold>
              ),
              renderCell: (params) => {
                const functionColor = params.row.functionColor;
                const isSendToFlex = params.row.isSendToFlex;
                const isRosterMatch = params.row.isRosterMatch;
                const isParked = params.row.isParked;
                const isSelf = params.row.isSelf;
                const openForSelfMatch = params.row.isOpenForSelfMatch;

                const displayStatus: RowDisplayStatus[] = [
                  {
                    condition: isParked,
                    label: t('dataGrid.tooltip.rowIsParked'),
                    icon: 'ban'
                  },
                  {
                    condition: isSendToFlex,
                    label: t('dataGrid.tooltip.sendToFlex'),
                    icon: 'arrow-right-arrow-left'
                  },
                  {
                    condition: isRosterMatch,
                    label: t('dataGrid.tooltip.rosterMatch'),
                    icon: 'message'
                  },
                  {
                    condition: isSelf,
                    label: t('dataGrid.tooltip.self'),
                    icon: 'link'
                  },
                  {
                    condition: openForSelfMatch,
                    label: t('dataGrid.tooltip.openForSelfMatch'),
                    icon: 'users'
                  }
                ];

                const isAnyStatus = displayStatus.some(
                  (status) => status.condition
                );

                const tooltipMessage = isSendToFlex
                  ? t('dataGrid.tooltip.sendToFlex')
                  : t('dataGrid.tooltip.rosterMatch');

                return (
                  <Tooltip
                    title={tooltipMessage}
                    disableHoverListener={!isAnyStatus}
                    arrow
                  >
                    <StyledOpenRequestFunctionBox>
                      {functionColor && (
                        <StyledEntityColorIndicator
                          functionColor={functionColor}
                        />
                      )}
                      <Typography variant="body2">{params.value}</Typography>
                      <StyledBoxFlexSmallGap>
                        {displayStatus.map((status) => {
                          return status.condition ? (
                            <IconContainer
                              key={status.label}
                              iconSize="SMALL"
                              iconColor="inherit"
                              title={status.label}
                            >
                              <Icon type={status.icon} color="inherit" />
                            </IconContainer>
                          ) : null;
                        })}
                      </StyledBoxFlexSmallGap>
                    </StyledOpenRequestFunctionBox>
                  </Tooltip>
                );
              }
            }
          ]
        : [
            {
              field: 'employee',
              headerName: t('dataGrid.employee'),
              minWidth: 220,
              flex: 1,
              valueGetter: (_, row) =>
                formatEmployeeName(row, employeeNameFormat),
              renderHeader: () => (
                <EmployeeHeader
                  showSourceCostCenter={showSourceCostCenter}
                  showCommitmentCostCenter={showCommitmentCostCenter}
                />
              ),
              renderCell: (params) => {
                return (
                  <EmployeeCell
                    params={params}
                    showSourceCostCenter={showSourceCostCenter}
                    showCommitmentCostCenter={showCommitmentCostCenter}
                    employeeNameFormat={employeeNameFormat}
                  />
                );
              }
            }
          ]),
      ...datesToDisplay.map((date): GridColDef<GridValidRowModel> => {
        const isWeekend = dayjs(date).day() === 6 || dayjs(date).day() === 0;
        const isSaturday = dayjs(date).day() === 6;
        const isSunday = dayjs(date).day() === 0;
        const isMonday = dayjs(date).day() === 1;
        const isToday = dayjs(date).isSame(dayjs(), 'day');
        const lastDayOfRange = dayjs(date).isSame(
          dayjs(datesToDisplay[datesToDisplay.length - 1])
        );

        return {
          field: date,
          headerName: dayjs(date).format('DD MMM'),
          flex: 1,
          minWidth: period === 'month' ? 50 : 100,
          maxWidth: period === 'month' ? 50 : undefined,
          headerAlign: 'center',
          align: 'center',
          sortable: false,
          filterable: false,
          pinnable: false,
          hideable: false,
          disableColumnMenu: true,
          headerClassName: `${isOpenRequests ? 'dates-columns' : ''} ${
            isSaturday
              ? 'saturday-header'
              : isSunday
              ? 'sunday-header'
              : isMonday
              ? 'monday-header'
              : ''
          }${lastDayOfRange ? ' last-day-header' : ''}`,
          cellClassName: `day-cell${
            isToday ? ' today' : isWeekend ? ' weekend' : ''
          }${lastDayOfRange ? ' last-day-cell' : ''}`,
          renderHeader: () => <DayHeader date={date} isToday={isToday} />,
          renderCell: (params) => {
            const days: EmployeeDay[] = params.row?.days || [];
            const absencesForTheDay =
              days.find((day: EmployeeDay) =>
                dayjs(day.date).isSame(date, 'day')
              )?.absences || [];

            const absencesInternal = absencesForTheDay?.filter(
              (absence) => !absence.flags.includes('EXTERNAL')
            );

            const absencesExternal = absencesForTheDay?.filter((absence) =>
              absence.flags.includes('EXTERNAL')
            );

            const isAbsentInternalToday = absencesInternal?.length > 0;

            const isAbsentExternalToday = absencesExternal?.length > 0;

            return (
              <>
                {isAbsentInternalToday && (
                  <Tooltip
                    title={t('dataGrid.tooltip.employeeIsAbsent', {
                      employee: capitalize(params.row.nameInformal)
                    })}
                    arrow
                  >
                    <StyledAbsenceBox />
                  </Tooltip>
                )}
                <StyledDutyCellBox>
                  <DutyCell
                    params={params}
                    date={date}
                    isOpenRequest={isOpenRequests}
                    qualificationsByCostCenters={qualificationsByCostCenters}
                    costCenterFromOthersPrefValue={
                      costCenterFromOthersPrefValue
                    }
                    isAbsentToday={
                      isAbsentInternalToday || isAbsentExternalToday
                    }
                  />
                </StyledDutyCellBox>
                {isAbsentExternalToday && (
                  <Tooltip
                    title={t('dataGrid.tooltip.employeeIsAbsent', {
                      employee: capitalize(params.row.nameInformal)
                    })}
                    arrow
                  >
                    <StyledAbsenceBox isExternal={isAbsentExternalToday} />
                  </Tooltip>
                )}
              </>
            );
          }
        };
      }),
      ...(showCommitmentHours
        ? [
            {
              field: 'commitmentHours',
              headerName: t('dataGrid.commitmentHours'),
              ...commonFieldProps,
              renderCell: ({ row }) => {
                const { days } = row;
                const totalHours = isOpenRequests
                  ? days?.reduce((acc: number, day) => {
                      const dayHours = day.hoursForCalculation || 0;
                      return acc + dayHours;
                    }, 0) || 0
                  : days?.reduce((acc: number, day) => {
                      const dayHours =
                        day.duties?.reduce((dayAcc: number, duty) => {
                          return (
                            dayAcc +
                            ((!duty.flags.includes('STATUS_BLOCKED') &&
                              duty.periods?.reduce(
                                (acc: number, period) =>
                                  acc + (period.hours || 0),
                                0
                              )) ||
                              0)
                          );
                        }, 0) || 0;
                      return acc + dayHours;
                    }, 0) || 0;
                const totalHoursToFixed = totalHours.toFixed(1);

                return (
                  <StyledTypographyFontMedium component="span">
                    {totalHours ? totalHoursToFixed : 0}
                  </StyledTypographyFontMedium>
                );
              }
            }
          ]
        : []),
      ...(isOpenRequests
        ? PLACEHOLDER_KEYS.map((key) => makeEmptyCol(key))
        : [
            ...(showBalanceWeek && weekOverview
              ? [
                  {
                    field: 'balanceWeek',
                    headerName: t('dataGrid.commitmentBalanceWeek'),
                    ...commonFieldProps,
                    renderCell: ({ row }) => {
                      const { balance } = row;
                      return dataChipValue({
                        value: balance,
                        flipValue: true,
                        tooltipValue: t('dataGrid.workedInsufficient')
                      });
                    }
                  }
                ]
              : []),
            ...(showBalanceMonth && !fourWeeksOverview
              ? [
                  {
                    field: 'balance',
                    headerName: t('dataGrid.commitmentBalance'),
                    ...commonFieldProps,
                    renderCell: ({ row }) => {
                      const { balance } = row;
                      return dataChipValue({
                        value: balance,
                        flipValue: true,
                        tooltipValue: t('dataGrid.workedInsufficient')
                      });
                    }
                  }
                ]
              : []),
            ...(showCumulativeBalance
              ? [
                  {
                    field: 'cumulative',
                    headerName: t('dataGrid.cumulativeBalance'),
                    ...commonFieldProps,
                    renderCell: ({ row }) => {
                      const { cumulative } = row;
                      return dataChipValue({
                        value: cumulative,
                        flipValue: true,
                        tooltipValue: t('dataGrid.workedInsufficient')
                      });
                    }
                  }
                ]
              : []),
            ...(showWorkedBalance
              ? [
                  {
                    field: 'worked',
                    headerName: t('dataGrid.workedBalance'),
                    ...commonFieldProps,
                    renderCell: ({ row }) => {
                      const { worked } = row;
                      return dataChipValue({
                        value: worked,
                        flipValue: true,
                        tooltipValue: t('dataGrid.workedInsufficient')
                      });
                    }
                  }
                ]
              : []),
            ...(showLeaveBalance
              ? [
                  {
                    field: 'leave',
                    headerName: t('dataGrid.leaveBalance'),
                    ...commonFieldProps,
                    renderCell: ({ row }) => {
                      const { leave } = row;
                      return dataChipValue({ value: leave });
                    }
                  }
                ]
              : []),
            ...(showYearlyLeaveBalance
              ? [
                  {
                    field: 'year',
                    headerName: t('dataGrid.yearlyLeaveBalance'),
                    ...commonFieldProps,
                    renderCell: ({ row }) => {
                      const { year } = row;
                      return dataChipValue({ value: year });
                    }
                  }
                ]
              : []),
            ...(showPlbBalance
              ? [
                  {
                    field: 'plb',
                    headerName: t('dataGrid.plbBalance'),
                    ...commonFieldProps,
                    renderCell: ({ row }) => {
                      const { plb } = row;
                      return dataChipValue({ value: plb });
                    }
                  }
                ]
              : []),
            ...(showPlbYearBalance
              ? [
                  {
                    field: 'plbYear',
                    headerName: t('dataGrid.plbYearBalance'),
                    ...commonFieldProps,
                    renderCell: ({ row }) => {
                      const { plbYear } = row;
                      return dataChipValue({ value: plbYear });
                    }
                  }
                ]
              : [])
          ])
    ],
    [
      t,
      datesToDisplay,
      period,
      isOpenRequests,
      qualificationsByCostCenters,
      showSourceCostCenter,
      showCommitmentCostCenter,
      costCenterFromOthersPrefValue,
      showCommitmentHours,
      showBalanceWeek,
      showBalanceMonth,
      showCumulativeBalance,
      showWorkedBalance,
      showLeaveBalance,
      showYearlyLeaveBalance,
      showPlbBalance,
      showPlbYearBalance,
      employeeNameFormat,
      showSortOrderColumn
    ]
  );
};
